import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { useUpsertCMChecklistManualOverride } from '~/hooks/apis/offers/useCMChecklistManualOverrides';

import type { CMChecklistManualOverride } from '~/services/OffersService';

import { isRowApproved } from './approvalQueueModel';
import type { GeneratedChecklistRow } from './checklistRows';
import { buildManualOverrideState } from './manualOverridePersistence';
import type { DealOptionsInclusionsChecklistRow } from './tabs/dealOptionsInclusions/types';
import type {
  ApprovalQueueItem,
  ManualOverrideDecision,
  ManualOverrideState,
  WithChecklistItemMetadata,
} from './types';

interface Input {
  offerId: string;
  manualOverridesFromApi: Array<CMChecklistManualOverride>;
  rows: Array<GeneratedChecklistRow>;
}

export function useChecklistApprovalState({ offerId, manualOverridesFromApi, rows }: Input) {
  const { enqueueSnackbar } = useSnackbar();
  const [expandedQueueItems, setExpandedQueueItems] = useState<Record<string, boolean>>({});
  const [manualOverrides, setManualOverrides] = useState<ManualOverrideState>({});
  const upsertManualOverride = useUpsertCMChecklistManualOverride(offerId);
  const rowById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);

  useEffect(() => {
    setManualOverrides(
      buildManualOverrideState({
        manualOverridesFromApi,
        rows,
      }),
    );
  }, [manualOverridesFromApi, rows]);

  const approveRow = useCallback(
    (rowId: string) => {
      const row = rowById.get(rowId);

      if (!row) {
        return;
      }

      const previousOverrideStatus = manualOverrides[rowId];
      const nextOverrideStatus: ManualOverrideDecision = 'approved';

      setManualOverrides((current) => ({ ...current, [rowId]: nextOverrideStatus }));

      upsertManualOverride.mutate(
        {
          evidence_hash: row.evidenceHash,
          is_approved: toApiManualOverrideApproval(nextOverrideStatus),
          item_key: row.itemKey,
          updated_by: 'ap:manual-approval',
        },
        {
          onError: (error) => {
            setManualOverrides((current) => restoreManualOverrideStatus(current, rowId, previousOverrideStatus));
            enqueueSnackbar(`Could not save approval: ${error.message}`, { variant: 'error' });
          },
        },
      );
    },
    [enqueueSnackbar, manualOverrides, rowById, upsertManualOverride],
  );

  const unapproveRow = useCallback(
    (rowId: string) => {
      const row = rowById.get(rowId);

      if (!row) {
        return;
      }

      const previousOverrideStatus = manualOverrides[rowId];
      const nextOverrideStatus: ManualOverrideDecision = 'requires_approval';

      setManualOverrides((current) => ({ ...current, [rowId]: nextOverrideStatus }));

      upsertManualOverride.mutate(
        {
          evidence_hash: row.evidenceHash,
          is_approved: toApiManualOverrideApproval(nextOverrideStatus),
          item_key: row.itemKey,
          updated_by: 'ap:manual-requires-approval',
        },
        {
          onError: (error) => {
            setManualOverrides((current) => restoreManualOverrideStatus(current, rowId, previousOverrideStatus));
            enqueueSnackbar(`Could not save approval reset: ${error.message}`, { variant: 'error' });
          },
        },
      );
    },
    [enqueueSnackbar, manualOverrides, rowById, upsertManualOverride],
  );

  const toggleQueueItemDetails = useCallback((itemId: string) => {
    setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }));
  }, []);

  const expandVisibleQueueItems = useCallback((visibleQueueItems: Array<ApprovalQueueItem>) => {
    setExpandedQueueItems((current) => {
      const shouldExpand = !visibleQueueItems.every((queueItem) => current[queueItem.id]);

      return visibleQueueItems.reduce(
        (next, queueItem) => ({
          ...next,
          [queueItem.id]: shouldExpand,
        }),
        current,
      );
    });
  }, []);

  const markDealOptionsInclusionsApproved = useCallback(
    async (rows: Array<WithChecklistItemMetadata<DealOptionsInclusionsChecklistRow>>) => {
      const rowsToApprove = rows.filter((row) => row.status !== 'blocked' && !isRowApproved(row, manualOverrides));
      const previousOverrideStatuses = new Map(rowsToApprove.map((row) => [row.id, manualOverrides[row.id]]));

      setManualOverrides((current) => {
        const rowIds = rowsToApprove
          .filter((row) => row.status !== 'blocked' && !isRowApproved(row, current))
          .map((row) => row.id);

        return {
          ...current,
          ...Object.fromEntries(rowIds.map((rowId) => [rowId, 'approved' as const])),
        };
      });

      const results = await Promise.allSettled(
        rowsToApprove.map((row) =>
          upsertManualOverride.mutateAsync({
            evidence_hash: row.evidenceHash,
            is_approved: toApiManualOverrideApproval('approved'),
            item_key: row.itemKey,
            updated_by: 'ap:manual-approval',
          }),
        ),
      );

      const failedRows = rowsToApprove.filter((_, index) => results[index].status === 'rejected');

      if (failedRows.length === 0) {
        return;
      }

      setManualOverrides((current) =>
        failedRows.reduce(
          (nextOverrides, row) =>
            restoreManualOverrideStatus(nextOverrides, row.id, previousOverrideStatuses.get(row.id)),
          current,
        ),
      );
      enqueueSnackbar(
        `Could not save ${failedRows.length} approval${failedRows.length === 1 ? '' : 's'}; reverted failed row${
          failedRows.length === 1 ? '' : 's'
        }.`,
        { variant: 'error' },
      );
    },
    [enqueueSnackbar, manualOverrides, upsertManualOverride],
  );

  return {
    approveRow,
    expandedQueueItems,
    expandVisibleQueueItems,
    manualOverrides,
    markDealOptionsInclusionsApproved,
    toggleQueueItemDetails,
    unapproveRow,
  };
}

function toApiManualOverrideApproval(status: ManualOverrideDecision) {
  return status === 'approved';
}

function restoreManualOverrideStatus(
  current: ManualOverrideState,
  rowId: string,
  previousOverrideStatus: ManualOverrideDecision | undefined,
): ManualOverrideState {
  if (previousOverrideStatus) {
    return { ...current, [rowId]: previousOverrideStatus };
  }

  const { [rowId]: omittedOverride, ...rest } = current;
  void omittedOverride;
  return rest;
}
